import React, { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { createClient } from '@supabase/supabase-js'

import AppShell from './components/AppShell'
import AuthPage from './pages/AuthPage'
// import ChatPage from './pages/ChatPage'
import ProfilePage from './pages/ProfilePage'
import ActionsPage from './pages/ActionsPage'
import TriagePage from './pages/TriagePage'
import AuthCallback from './pages/AuthCallback'
import TriageResults from './pages/TriageResults'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AssessmentPage from './pages/AssessmentPage'
import IntelligencePage from './pages/IntelligencePage'
// import SubjectsPage from './pages/SubjectsPage'
import AdvisorDashboard from './pages/AdvisorDashboard'
import AdminDashboard from './pages/AdminDashboard'
import CaseBriefPage from './pages/CaseBriefPage'
import { SubjectProvider } from './context/SubjectContext'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import CalculatorPage from './pages/CalculatorPage'
import AutonomoCalculatorPage from './pages/AutonomoCalculatorPage'
import StartPage from './pages/StartPage'
import IntakePage from './pages/IntakePage'
import AdvisorClientsPage from './pages/AdvisorClientsPage'
import AdvisorIntakePage from './pages/AdvisorIntakePage'
import { isIntakeRouteEnabled } from './lib/intake/intakeFlag'

import CalculatorSavedPage from './pages/CalculatorSavedPage'


const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

function LoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center bg-sand font-serif text-sm tracking-wider text-neutral-500">
      Loading…
    </div>
  )
}

// ── Shared user-type resolution ────────────────────────────────────────────────
// Reads from sessionStorage (written by AppShell on first load) to avoid
// a DB round-trip on every navigation. Falls back to a DB fetch on first visit.

function useUserType(user) {
  const [isPartner, setIsPartner] = useState(
    () => sessionStorage.getItem('hs_user_type') === 'partner'
  )
  const [checked, setChecked] = useState(
    () => !!sessionStorage.getItem('hs_user_type')
  )

  useEffect(() => {
    if (!user || checked) return
    supabase
      .from('profiles')
      .select('user_type')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        const type = data?.user_type || 'consumer'
        sessionStorage.setItem('hs_user_type', type)
        setIsPartner(type === 'partner')
        setChecked(true)
      })
  }, [user?.id, checked])  // stable: user.id is a string, won't change on token refresh

  return { isPartner, checked }
}

// ── Admin resolution ───────────────────────────────────────────────────────────
// Deliberately NOT cached in sessionStorage, unlike useUserType above.
// isPartner is checked on nearly every navigation (worth caching);
// isAdmin is only ever checked when someone loads /admin — there's no
// perf case for caching it, and caching is exactly what caused a real
// bug: flipping profiles.is_admin to true didn't take effect until
// sessionStorage was manually cleared, because the stale 'false' from an
// earlier visit this tab never got invalidated. Always fetch fresh.

function useIsAdmin(user) {
  const [isAdmin, setIsAdmin] = useState(false)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (!user) { setChecked(true); return }
    setChecked(false)
    supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        setIsAdmin(!!data?.is_admin)
        setChecked(true)
      })
  }, [user?.id])

  return { isAdmin, checked }
}

// ── ProtectedRoute ─────────────────────────────────────────────────────────────
// Consumer pages only. Partners are redirected to /advisor.

function ProtectedRoute({ children, requiresTriage = true, withShell = true }) {
  const { user, loading } = useAuth()
  const { isPartner, checked: typeChecked } = useUserType(user)
  const [triageChecked,  setTriageChecked]  = useState(false)
  const [triageComplete, setTriageComplete] = useState(false)

  useEffect(() => {
    if (!user) return
    supabase
      .from('tax_year_profiles')
      .select('triage_completed')
      .eq('user_id', user.id)
      .eq('triage_completed', true)
      .limit(1)
      .then(({ data, error }) => {
        setTriageComplete(!error && data && data.length > 0)
        setTriageChecked(true)
      })
  }, [user])

  if (loading || (user && !typeChecked) || (user && requiresTriage && !triageChecked))
    return <LoadingScreen />
  // Portal home for an anonymous visitor to a protected page is '/' (see
  // HomeRedirect/StartRoute below — same destination, same reasoning).
  // Distinct from the requiresTriage branch just below: that one sends an
  // authenticated-but-triage-incomplete user to finish a specific product,
  // not "home" — leave that pointed at /spainsnapshot.
  if (!user)     return <Navigate to="/"        replace />
  if (isPartner) return <Navigate to="/advisor" replace />
  if (requiresTriage && !triageComplete) return <Navigate to="/spainsnapshot" replace />

  return withShell ? <AppShell>{children}</AppShell> : children
}

// ── PartnerRoute ───────────────────────────────────────────────────────────────
// Advisor pages only. Consumers are redirected to /chat.

function PartnerRoute({ children, withShell = true }) {
  const { user, loading } = useAuth()
  const { isPartner, checked } = useUserType(user)

  if (loading || (user && !checked)) return <LoadingScreen />
  if (!user)      return <Navigate to="/auth"        replace />
  if (!isPartner) return <Navigate to="/intelligence"  replace />

  return withShell ? <AppShell>{children}</AppShell> : children
}

// ── AdminRoute ─────────────────────────────────────────────────────────────────
// Admin-only pages (currently just /admin). Not linked from any nav —
// reached only by typing the URL — but gated server-side regardless
// (get-admin-overview independently checks profiles.is_admin; this route
// guard is a UX convenience, not the real security boundary).
// Unauthenticated or non-admin users are sent to '/', same as every
// other route's "you don't belong here" destination.

function AdminRoute({ children }) {
  const { user, loading } = useAuth()
  const { isAdmin, checked } = useIsAdmin(user)

  if (loading || (user && !checked)) return <LoadingScreen />
  if (!user)    return <Navigate to="/" replace />
  if (!isAdmin) return <Navigate to="/" replace />

  return <AppShell>{children}</AppShell>
}

// ── HomeRedirect ───────────────────────────────────────────────────────────────
// Fallback route (catch-all, and /auth when already signed in). Sends
// partners to their dashboard; everyone else (anonymous or authenticated
// consumer) to '/', which is now the real front door — not a further
// redirect target itself. Previously routed authenticated-non-partner
// users to /profile; per portal_start_page_plan.md's reasoning (auth state
// is the wrong axis to route on — product state is), that distinction is
// gone: '/' reflects state for both anonymous and authenticated consumers
// identically.

function HomeRedirect() {
  const { user, loading } = useAuth()
  const { isPartner, checked } = useUserType(user)

  if (loading || (user && !checked)) return <LoadingScreen />
  if (isPartner) return <Navigate to="/advisor" replace />
  return           <Navigate to="/"         replace />
}

// ── StartRoute ─────────────────────────────────────────────────────────────────
// Portal front door ('/'). Public for anonymous + authenticated consumers;
// partners are redirected onward (same branch HomeRedirect uses).
//
// Always wrapped in AppShell, same reasoning as AuthAwareRoute just below:
// `user` flipping null → real object mid-session on this exact page (e.g.
// an embedded sign-in, same shape as the calculator/triage pages) must
// never change the DOM ancestor chain shape, or React unmounts and
// remounts StartPage with no navigation to signal it happened.

function StartRoute() {
  const { user, loading } = useAuth()
  const { isPartner, checked } = useUserType(user)

  if (loading || (user && !checked)) return <LoadingScreen />
  if (isPartner) return <Navigate to="/advisor" replace />

  return <AppShell><StartPage /></AppShell>
}

// ── AuthAwareRoute ─────────────────────────────────────────────────────────────
// Used for pages that are public but should show the app shell when logged in.
// Anonymous users: raw page (no header/footer).
// Authenticated users: page inside AppShell.
// No redirect in either direction — the page itself handles both modes.

function AuthAwareRoute({ children }) {
  const { loading } = useAuth()
  if (loading) return <LoadingScreen />
  // Always wrapped in AppShell now — AppShell itself decides how much
  // chrome to show for anonymous vs. authenticated users (see AppShell.js).
  // Previously this branched here (bare children vs. <AppShell>children
  // </AppShell>), which meant the moment `user` went from null to a real
  // object mid-session (e.g. completing the OTP flow inline on this same
  // page, no navigation involved), the page's ancestor chain changed shape
  // — direct child of a Fragment, then nested inside AppShell's DOM. React
  // can't reuse a component instance across a structural change like that;
  // it fully unmounts and remounts `children`, silently wiping out
  // whatever local state that page held (e.g. CalculatorPage's in-progress
  // result) with no navigation and no URL change to signal it happened.
  return <AppShell>{children}</AppShell>
}

// ── Legacy route redirects ──────────────────────────────────────────────────
// /start was renamed to /spainsnapshot once the calculator became a second
// free product (see App.js history). These preserve query strings and
// params so old magic links, shared ?ref= partner links, and bookmarks
// keep working rather than silently dropping data on redirect.

function LegacyRedirect({ to }) {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}`} replace />
}

function LegacyStartResultsRedirect() {
  const { publicToken } = useParams()
  const location = useLocation()
  return <Navigate to={`/spainsnapshot/results/${publicToken}${location.search}`} replace />
}

function AppRoutes() {
  const { user } = useAuth()

  return (
    <Routes>
      <Route path="/auth" element={user ? <HomeRedirect /> : <AuthPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Advisor Intake (ticket 061). Absent unless the build flag is exactly "true";
          then /intake falls through to the catch-all below. Public, and deliberately
          outside AuthAwareRoute / ProtectedRoute / AppShell. */}
      {isIntakeRouteEnabled(process.env.REACT_APP_INTAKE_ENABLED) && (
        <Route path="/intake" element={<IntakePage />} />
      )}
      {/* Advisor Intake I9 (ticket 073): an advisor's own clients. Same flag as /intake;
          partners only. /advisor/intake has no app header (it is the intake page). */}
      {isIntakeRouteEnabled(process.env.REACT_APP_INTAKE_ENABLED) && (
        <Route path="/advisor/clients" element={<PartnerRoute><AdvisorClientsPage /></PartnerRoute>} />
      )}
      {isIntakeRouteEnabled(process.env.REACT_APP_INTAKE_ENABLED) && (
        <Route path="/advisor/intake" element={<PartnerRoute withShell={false}><AdvisorIntakePage /></PartnerRoute>} />
      )}
      <Route path="/terms"   element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Public — no auth required; shell shown for logged-in users */}
      <Route path="/spainsnapshot" element={<AuthAwareRoute><TriagePage /></AuthAwareRoute>} />

      <Route path="/calculator" element={<AuthAwareRoute><CalculatorPage /></AuthAwareRoute>} />
      <Route path="/calculator/result/:token" element={<AuthAwareRoute><CalculatorSavedPage /></AuthAwareRoute>} />

      <Route path="/autonomo-calculator" element={<AuthAwareRoute><AutonomoCalculatorPage /></AuthAwareRoute>} />


      <Route path="/auth/callback"               element={<AuthCallback />} />
      <Route path="/spainsnapshot/results/:publicToken" element={<AuthAwareRoute><TriageResults /></AuthAwareRoute>} />

      {/* Legacy redirects — /start was the original route name from when
          triage was the only free product. Kept so old magic links,
          shared partner (?ref=) links, and bookmarks still resolve.
          Query strings are preserved explicitly — Navigate's plain string
          form strips them, which would silently break ?ref= partner links. */}
      <Route path="/start" element={<LegacyRedirect to="/spainsnapshot" />} />
      <Route path="/start/results/:publicToken" element={<LegacyStartResultsRedirect />} />

      {/* Consumer app routes */}
      {/* <Route path="/chat"       element={<ProtectedRoute requiresTriage={false}><ChatPage /></ProtectedRoute>} /> */}
      <Route path="/profile"    element={<ProtectedRoute requiresTriage={false}><ProfilePage /></ProtectedRoute>} />
      {/* <Route path="/actions"    element={<ProtectedRoute requiresTriage={false}><ActionsPage /></ProtectedRoute>} /> */}
      <Route path="/intelligence" element={<ProtectedRoute requiresTriage={false}><IntelligencePage /></ProtectedRoute>} />
      <Route path="/assessment"   element={<ProtectedRoute requiresTriage={false}><AssessmentPage /></ProtectedRoute>} />
      {/* <Route path="/subjects"   element={<ProtectedRoute requiresTriage={false}><SubjectsPage /></ProtectedRoute>} /> */}

      {/* Partner / advisor routes — gated to user_type = 'partner' */}
      <Route path="/advisor"                       element={<PartnerRoute><AdvisorDashboard /></PartnerRoute>} />
      <Route path="/advisor/subjects/:subjectId"   element={<PartnerRoute><CaseBriefPage /></PartnerRoute>} />

      {/* Admin route — gated to profiles.is_admin. Not linked from any
          nav; server-side check in get-admin-overview is the real gate. */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

      <Route path="/" element={<StartRoute />} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  )
}


export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubjectProvider>
          <AppRoutes />
        </SubjectProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}